/*
 * Menu pages' floating section pill (snippets/menu-subnav.liquid): marks the
 * link of the section currently in view with aria-current and scrolls that
 * link into view inside the bar when the links overflow sideways, showing
 * an arrow at each edge that has more links past it. Once the
 * footer scrolls into view the pill slides away, and it comes back as soon as
 * the reader scrolls up. The links are plain anchors, so the bar works
 * without this file.
 */
(() => {
  const nav = document.querySelector("[data-menu-subnav]");
  if (!nav) return;

  // Hide at the bottom of the page, show again on any scroll up. Keep it
  // while it holds focus (an open Order online list, a keyboard user).
  const footer = document.querySelector(".site-footer");
  if (footer && "IntersectionObserver" in window) {
    let footerInView = false;
    let scrollingDown = false;
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 2) {
        scrollingDown = y > lastY;
        lastY = y;
      }
      if (footerInView && scrollingDown && !nav.contains(document.activeElement)) {
        nav.setAttribute("data-hidden", "");
      } else {
        nav.removeAttribute("data-hidden");
      }
    };

    new IntersectionObserver(([entry]) => {
      footerInView = entry.isIntersecting;
      update();
    }).observe(footer);

    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true },
    );
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  // The weekly menu floats only the Order online button, with no links.
  const list = nav.querySelector("[data-menu-subnav-links]");
  if (!list) return;
  const links = [...nav.querySelectorAll("[data-menu-subnav-link]")];

  // Edge arrows: each shows while there are more links past that edge and
  // moves the row by most of its width. CSS shows them only on phones.
  const prev = nav.querySelector("[data-menu-subnav-prev]");
  const next = nav.querySelector("[data-menu-subnav-next]");
  const updateArrows = () => {
    const max = list.scrollWidth - list.clientWidth;
    prev.hidden = list.scrollLeft <= 4;
    next.hidden = list.scrollLeft >= max - 4;
  };
  const step = (direction) => {
    list.scrollBy({
      left: direction * list.clientWidth * 0.7,
      behavior: reduceMotion.matches ? "auto" : "smooth",
    });
  };
  prev.addEventListener("click", () => step(-1));
  next.addEventListener("click", () => step(1));
  list.addEventListener("scroll", updateArrows, { passive: true });
  if ("ResizeObserver" in window) new ResizeObserver(updateArrows).observe(list);
  updateArrows();
  const sections = links
    .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
    .filter(Boolean);
  if (!sections.length || !("IntersectionObserver" in window)) return;

  let current = null;

  function setCurrent(id) {
    if (id === current) return;
    current = id;
    for (const link of links) {
      const active = link.hash.slice(1) === id;
      if (active) {
        link.setAttribute("aria-current", "true");
        const left = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
        list.scrollTo({ left, behavior: reduceMotion.matches ? "auto" : "smooth" });
      } else {
        link.removeAttribute("aria-current");
      }
    }
  }

  // A section is current while it crosses a band 30-40% down the screen;
  // the first one in that band wins.
  const visible = new Set();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      const inView = sections.filter((section) => visible.has(section));
      if (inView.length) setCurrent(inView[0].id);
    },
    { rootMargin: "-30% 0px -60% 0px" },
  );
  for (const section of sections) observer.observe(section);
})();
